import type { Component, ComponentProps } from "solid-js";
import { Bike, Car, Home, Package, WashingMachine } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { AssetKind, DueStatus } from "@tendly/shared";

/** Lucide icon by asset kind. */
const ASSET_KIND_ICONS: Record<AssetKind, Component<ComponentProps<"svg">>> = {
  car: Car,
  motorcycle: Bike,
  home: Home,
  appliance: WashingMachine,
  other: Package,
};

const KIND_ICON_LOOKUP: Record<string, Component<ComponentProps<"svg">> | undefined> =
  ASSET_KIND_ICONS;

export function assetKindIcon(kind: string | null | undefined): Component<ComponentProps<"svg">> {
  return KIND_ICON_LOOKUP[kind ?? "other"] ?? Package;
}

/** Translatable labels by asset kind (loose lookup: unknown keys → undefined). */
export function assetKindLabels(
  t: (key: MessageDescriptor) => string,
): Record<string, string | undefined> {
  return {
    car: t(msg`Car`),
    motorcycle: t(msg`Motorcycle`),
    home: t(msg`Home`),
    appliance: t(msg`Appliance`),
    other: t(msg`Other`),
  };
}

/** Translatable labels by service category (loose lookup). */
export function serviceCategoryLabels(
  t: (key: MessageDescriptor) => string,
): Record<string, string | undefined> {
  return {
    maintenance: t(msg`Maintenance`),
    repair: t(msg`Repair`),
    inspection: t(msg`Inspection`),
    insurance: t(msg`Insurance`),
    tax: t(msg`Tax`),
    upgrade: t(msg`Upgrade`),
    other: t(msg`Other`),
  };
}

/** Usage unit label. */
export function usageUnitLabel(
  t: (key: MessageDescriptor) => string,
  unit: string | null | undefined,
): string {
  if (unit === "km") {
    return t(msg`km`);
  }
  if (unit === "hours") {
    return t(msg`hours`);
  }
  return "";
}

/** Color classes by due status (badge / icon). */
export const DUE_STATUS_CLASSES: Record<DueStatus, string> = {
  overdue: "bg-danger/15 text-danger ring-danger/30",
  due_soon: "bg-warning/15 text-warning ring-warning/30",
  ok: "bg-success/10 text-success ring-success/20",
  unknown: "bg-muted/30 text-muted-foreground ring-border/30",
};

/** Urgency lateral border for "upcoming" row. */
export const DUE_STATUS_EDGE: Record<DueStatus, string> = {
  overdue: "border-l-danger",
  due_soon: "border-l-warning",
  ok: "border-l-success/60",
  unknown: "border-l-border",
};

export function dueStatusLabels(t: (key: MessageDescriptor) => string): Record<DueStatus, string> {
  return {
    overdue: t(msg`Overdue`),
    due_soon: t(msg`Due soon`),
    ok: t(msg`On track`),
    unknown: t(msg`Needs data`),
  };
}
